import { Bot, Database, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { KnowledgeBaseWithCount } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/date";

interface Props {
  kb: KnowledgeBaseWithCount;
  ownerTeamName?: string;
  ownerTeamColor?: string | null;
}

export function KbCard({ kb, ownerTeamName, ownerTeamColor }: Props) {
  const { t } = useTranslation("knowledgeBases");
  return (
    <Link
      to={`/knowledge-bases/${kb.id}`}
      className="flex h-full flex-col rounded-md border bg-card p-4 shadow-sm transition hover:border-primary"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-md"
            style={{
              backgroundColor: ownerTeamColor
                ? `${ownerTeamColor}1A`
                : "hsl(var(--muted))",
              color: ownerTeamColor ?? "hsl(var(--foreground))",
            }}
            aria-hidden
          >
            <Database className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-semibold">{kb.name}</h2>
            <p className="text-xs text-muted-foreground">
              {ownerTeamName ?? t("card.noTeam")}
            </p>
          </div>
        </div>
        {!kb.is_active ? (
          <Badge variant="destructive">{t("card.inactive")}</Badge>
        ) : null}
      </div>
      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
        {kb.short_description ?? t("card.noDescription")}
      </p>
      <div className="mt-auto pt-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {t("document.count", { count: kb.document_count })}
          </span>
          <span className="inline-flex items-center gap-1">
            <Bot className="h-3 w-3" />
            {t("card.agentCount", { count: kb.agent_count })}
          </span>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t("card.dates", {
            created: formatDate(kb.created_at),
            updated: formatDate(kb.updated_at),
          })}
        </p>
      </div>
    </Link>
  );
}
